import type { IconType } from '@react-icons/all-files/lib'
import { LuDisc3 } from '@react-icons/all-files/lu/LuDisc3'
import { LuGauge } from '@react-icons/all-files/lu/LuGauge'
import { LuGuitar } from '@react-icons/all-files/lu/LuGuitar'
import { LuMicVocal } from '@react-icons/all-files/lu/LuMicVocal'
import { LuSmile } from '@react-icons/all-files/lu/LuSmile'
import { LuTag } from '@react-icons/all-files/lu/LuTag'

/**
 * Radix Theme accent tokens (not hex) so category colors adapt in dark mode.
 * @see https://www.radix-ui.com/themes/docs/theme/color
 */
export type CategoryAccent =
  | 'ruby'
  | 'amber'
  | 'violet'
  | 'teal'
  | 'blue'
  | 'gray'
  | 'green'
  | 'orange'
  | 'indigo'
  | 'pink'

type CategoryMeta = {
  icon: IconType
  accent: CategoryAccent
}

// Category names are arbitrary, business-defined tag categories — this only
// covers a few common ones so they visually match the design exploration
// they were modeled after. Anything else falls back to a generic icon and a
// deterministically-assigned accent (see getFallbackAccent below).
const KNOWN_CATEGORIES: Record<string, CategoryMeta> = {
  mood: { icon: LuSmile, accent: 'ruby' },
  instrument: { icon: LuGuitar, accent: 'amber' },
  vocals: { icon: LuMicVocal, accent: 'violet' },
  tempo: { icon: LuGauge, accent: 'teal' },
  genre: { icon: LuDisc3, accent: 'blue' },
}

const FALLBACK_ICON: IconType = LuTag

const FALLBACK_ACCENTS: CategoryAccent[] = [
  'gray',
  'green',
  'orange',
  'indigo',
  'pink',
  'blue',
  'teal',
  'violet',
  'amber',
  'ruby',
]

export const getKnownCategoryMeta = (
  name: string | undefined,
): CategoryMeta | undefined =>
  name ? KNOWN_CATEGORIES[name.trim().toLowerCase()] : undefined

/**
 * `fallbackIndex` should be the position of this category among only the
 * *unmatched* categories (stable per render, not random) so fallback accents
 * don't collide unnecessarily with each other.
 */
export const getFallbackCategoryMeta = (
  fallbackIndex: number,
): CategoryMeta => ({
  icon: FALLBACK_ICON,
  // The modulo keeps the index in range; the `??` only satisfies
  // noUncheckedIndexedAccess and is unreachable for non-negative indices.
  accent: FALLBACK_ACCENTS[fallbackIndex % FALLBACK_ACCENTS.length] ?? 'gray',
})
